import React, {
  ChangeEvent,
  Children,
  cloneElement,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { useFormContext } from 'react-hook-form';
import cx from 'classnames';

import { getError } from '@/utils';

import ErrorText from '../ErrorText';

import './styles.css';
import { RadioBoxOptionProps } from '@/components/RadioBoxOption';

type RadioBoxProps<T> = {
  children?: React.ReactElement<RadioBoxOptionProps<T>>[];
  name: string;
  value?: T;
  defaultValue?: T;
  className?: string;
  onChange?(event: ChangeEvent<HTMLInputElement>, value: T): void;
};

const RadioBox = <T,>({
  children,
  name,
  value,
  className,
  onChange,
}: RadioBoxProps<T>) => {
  const {
    formState: { errors = {} },
    getValues,
    setValue,
    register,
  } = useFormContext() || {};
  const error = getError(errors, name);
  const errorId = `error-${name}`;
  const classNames = cx('flex flex-wrap ', className);

  const [rawValue, setRawValue] = useState<T>(getValues?.(name) ?? value);

  useLayoutEffect(() => {
    register?.(name);
  }, [register, name]);

  useEffect(() => {
    setValue?.(name, rawValue);
  }, [name, setValue, rawValue]);

  const options = Children.map(children || [], (child, index) => {
    const childClassName = cx(
      child.props.className,
      index === 0 ? 'pb-2' : 'pl-2 pb-2',
    );

    return cloneElement(child, {
      ...child.props,
      name,
      className: childClassName,
      checked: JSON.stringify(rawValue) === JSON.stringify(child.props.value),
      onChange(event: ChangeEvent<HTMLInputElement>, nextValue: T) {
        setRawValue(nextValue);
        onChange?.(event, nextValue);
      },
    });
  });

  return (
    <div>
      <div role='radiogroup' className={classNames}>
        {options}
      </div>
      {error && <ErrorText id={errorId}>{error}</ErrorText>}
    </div>
  );
};

export default RadioBox;
